import { DeleteOutlined } from "@ant-design/icons"
import {
  Alert,
  Button,
  Checkbox,
  Empty,
  Flex,
  Input,
  Popconfirm,
  Skeleton,
  Switch,
  Tooltip,
  Typography,
  notification
} from "antd"
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState
} from "react"
import { useTranslation } from "react-i18next"

import { EntityTable } from "../../../../components/EntityTable/EntityTable.tsx"
import { TABLE_SURFACES } from "../../../../components/EntityTable/surfaces.ts"
import type { ManagedListKey, ManagedListOption } from "./list-options.types.ts"
import { useManagedListOptions } from "./useManagedListOptions.ts"

const { Text } = Typography

/** The row the table shows while a new value is being named. */
const DRAFT_ID = "__draft__"

export interface ManagedListSectionHandle {
  startAdd: () => void
}

export interface ManagedListSectionProps {
  listKey: ManagedListKey
  title: string
  description: string
  /** Lead sources only — the column asks nothing on other lists. */
  showRequiresDetail?: boolean
  canCreate?: boolean
  canEdit?: boolean
  canDelete?: boolean
}

export const ManagedListSection = forwardRef<
  ManagedListSectionHandle,
  ManagedListSectionProps
>(function ManagedListSection(
  {
    listKey,
    title,
    description,
    showRequiresDetail = false,
    canCreate = false,
    canEdit = false,
    canDelete = false
  },
  ref
) {
  const { t } = useTranslation()
  const {
    options,
    loading,
    loadFailed,
    truncated,
    saving,
    reload,
    add,
    patch,
    remove
  } = useManagedListOptions(listKey)
  const [draft, setDraft] = useState("")
  const [adding, setAdding] = useState(false)
  const [renaming, setRenaming] = useState<Record<string, string>>({})

  const startAdd = useCallback(() => {
    if (!canCreate) return
    setDraft("")
    setAdding(true)
  }, [canCreate])

  useImperativeHandle(ref, () => ({ startAdd }), [startAdd])

  /** Awaits a write and reports a refusal. True when it went through. */
  const run = useCallback(
    async (work: Promise<{ message: string } | null>, errorKey: string) => {
      const failure = await work
      if (!failure) return true
      notification.error({ message: failure.message || t(errorKey) })
      return false
    },
    [t]
  )

  const onAdd = async () => {
    const label = draft.trim()
    if (!label) {
      setAdding(false)
      return
    }
    if (await run(add(label), "settingsLists.errors.add")) {
      setDraft("")
      setAdding(false)
    }
  }

  const clearTypedName = (id: string) =>
    setRenaming((prev) => {
      const next = { ...prev }
      delete next[id]
      return next
    })

  const onRename = async (option: ManagedListOption) => {
    const label = (renaming[option.id] ?? "").trim()
    if (!label || label === option.label) {
      clearTypedName(option.id)
      return
    }
    // Dropped only once the server has taken it: a refused rename would
    // otherwise leave nothing to retype from but a toast.
    if (await run(patch(option.id, { label }), "settingsLists.errors.rename")) {
      clearTypedName(option.id)
    }
  }

  const columns = [
    {
      title: t("settingsLists.columns.name"),
      dataIndex: "label",
      render: (_: unknown, option: ManagedListOption) =>
        option.id === DRAFT_ID ? (
          <Input
            aria-label={t("settingsLists.addPlaceholder", { list: title })}
            autoFocus
            disabled={saving}
            maxLength={255}
            onBlur={() => void onAdd()}
            onChange={(event) => setDraft(event.target.value)}
            onPressEnter={(event) => event.currentTarget.blur()}
            placeholder={t("settingsLists.addPlaceholder", { list: title })}
            value={draft}
          />
        ) : canEdit ? (
          <Input
            aria-label={t("settingsLists.columns.name")}
            disabled={saving}
            onBlur={() => void onRename(option)}
            onChange={(event) =>
              setRenaming((prev) => ({
                ...prev,
                [option.id]: event.target.value
              }))
            }
            onPressEnter={(event) => event.currentTarget.blur()}
            value={renaming[option.id] ?? option.label}
          />
        ) : (
          <Text>{option.label}</Text>
        )
    },
    ...(showRequiresDetail
      ? [
          {
            title: (
              <Tooltip title={t("settingsLists.columns.detailsHint")}>
                <span>{t("settingsLists.columns.details")}</span>
              </Tooltip>
            ),
            dataIndex: "requires_detail",
            width: 140,
            render: (_: unknown, option: ManagedListOption) =>
              option.id === DRAFT_ID ? null : (
                <Checkbox
                  aria-label={`${t("settingsLists.columns.details")} — ${option.label}`}
                  checked={option.requires_detail}
                  disabled={!canEdit || saving}
                  onChange={(event) =>
                    void run(
                      patch(option.id, {
                        requires_detail: event.target.checked
                      }),
                      "settingsLists.errors.details"
                    )
                  }
                />
              )
          }
        ]
      : []),
    {
      title: t("settingsLists.columns.active"),
      dataIndex: "is_active",
      width: 120,
      render: (_: unknown, option: ManagedListOption) =>
        option.id === DRAFT_ID ? null : (
          <Tooltip
            title={
              option.is_system ? t("settingsLists.systemProtected") : undefined
            }
          >
            <Switch
              aria-label={`${t("settingsLists.columns.active")} — ${option.label}`}
              checked={option.is_active}
              disabled={!canEdit || option.is_system || saving}
              onChange={(checked) =>
                void run(
                  patch(option.id, { is_active: checked }),
                  "settingsLists.errors.retire"
                )
              }
            />
          </Tooltip>
        )
    },
    {
      title: "",
      width: 60,
      render: (_: unknown, option: ManagedListOption) =>
        option.id === DRAFT_ID ? null : (
          <Popconfirm
            okText={t("settingsLists.actions.confirmDelete")}
            onConfirm={() =>
              void run(remove(option.id), "settingsLists.errors.delete")
            }
            title={t("settingsLists.actions.deleteTitle")}
          >
            <Button
              aria-label={`${t("settingsLists.actions.delete")} — ${option.label}`}
              danger
              disabled={!canDelete || option.is_system || saving}
              icon={<DeleteOutlined />}
              size={"small"}
              type={"text"}
            />
          </Popconfirm>
        )
    }
  ]

  if (loading) return <Skeleton active paragraph={{ rows: 4 }} />

  if (loadFailed) {
    return (
      <Alert
        action={
          <Button onClick={() => void reload()} size={"small"}>
            {t("settingsLists.retry")}
          </Button>
        }
        message={t("settingsLists.loadError")}
        type={"error"}
      />
    )
  }

  return (
    <Flex gap={"middle"} vertical>
      <Text type={"secondary"}>{description}</Text>
      {truncated && (
        <Alert
          message={t("settingsLists.truncated", { shown: options.length })}
          type={"warning"}
        />
      )}
      <EntityTable<ManagedListOption>
        columns={columns}
        dataSource={
          adding ? [...options, { id: DRAFT_ID } as ManagedListOption] : options
        }
        hideConfigButton
        locale={{
          emptyText: (
            <Empty
              description={t("settingsLists.empty", { list: title })}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            >
              {canCreate && (
                <Button onClick={startAdd} type={"primary"}>
                  {t("settingsLists.actions.create")}
                </Button>
              )}
            </Empty>
          )
        }}
        lockColumnLayout
        pagination={false}
        rowKey={"id"}
        scroll={{ x: "max-content" }}
        size={"small"}
        surface={TABLE_SURFACES.managedListOptions}
        tab={listKey}
        withOutPagination
      />
    </Flex>
  )
})
